<script setup lang="ts">
import type { AgentExclusivePropertiesSectionProps } from '.';
import type { Property } from '~/core/entities/property';

import {
	buildAgentExclusivePropertiesSrpPath,
	clearAgentExclusivesCarouselIndex,
	getAgentExclusivesCarouselIndex,
	isReturnNavigation,
	saveAgentExclusivesCarouselIndex,
	useAgentExclusiveProperties
} from '~/composables/agentExclusiveProperties';
import { useCarouselTracking } from '~/composables/events/useCarouselTracking';
import { useEvents } from '~/composables/events/useEvents';
import { SHORTS_ENTRY_SOURCE, useOpenShortsFeed } from '~/composables/shorts';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { buildCarouselTrackingContext } from '~/composables/useListingEventTracking';
import { MixpanelProps } from '~/core/service/mixpanel';
import { cn } from '~/utils';

import { ExclusivePropertyCard } from '~/components/ExclusivePropertyCard';
import { StandardSlider } from '~/components/StandardSlider';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';

import IconMaterialSymbolsLightLockOutline from '~icons/material-symbols-light/lock-outline';

import SectionSkeleton from './SectionSkeleton.vue';

const SECTION_NAME = 'agent profile exclusives';
const VIEW_ALL_CTA_TEXT = 'Ver todos';

const props = defineProps<AgentExclusivePropertiesSectionProps>();

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const { trackItemClicked, trackNavigated } = useCarouselTracking(
	'agent_exclusive_properties',
	SECTION_NAME
);
const { ft_srp_agent_filter: isAgentFilterEnabled } = useFeatureFlags();

const exclusives = useAgentExclusiveProperties(() => props.agentSlug);
const { properties, total, isLoading, isLoaded, hasProperties } = exclusives;

/**
 * O perfil não é uma rota de shorts e o chunk do player fica fora dela por
 * decisão de bundle. Sem exclusivos o "Explorar em vídeo" nunca é renderizado,
 * então o aquecimento acompanha o conteúdo em vez de rodar em toda visita.
 */
const { openShortsFeed } = useOpenShortsFeed({
	shouldWarmPlayerChunk: hasProperties
});

/**
 * Enquanto a request não responde o skeleton ocupa o lugar; depois dela, a
 * seção some por completo quando o corretor não tem exclusivos.
 */
const isInitialLoading = computed<boolean>(
	() => !isLoaded.value || isLoading.value
);

/**
 * Sem a flag da SRP o middleware remove o `agentSlug` da URL, e o "Ver todos"
 * abriria uma busca que não corresponde ao carrossel. Melhor não oferecer o
 * atalho do que levar o visitante a uma lista diferente.
 */
const viewAllHref = computed<string>(() =>
	isAgentFilterEnabled
		? buildAgentExclusivePropertiesSrpPath(props.agentSlug)
		: ''
);

/**
 * O carrossel mostra no máximo uma página da busca. Quando ela já cabe inteira
 * nele, o "Ver todos" levaria à mesma lista, sem nada a mais para ver.
 */
const hasMoreThanCarousel = computed<boolean>(
	() => total.value > properties.value.length
);

const trackingContext = computed(() =>
	buildCarouselTrackingContext({
		placement: 'perfil_corretor_exclusivos',
		properties: properties.value
	})
);

function handleViewAllClick(): void {
	rememberCarouselPosition();

	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: VIEW_ALL_CTA_TEXT,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: SECTION_NAME,
		[MixpanelProps.DESTINATION_URL]: viewAllHref.value
	});
}

/**
 * Entrega ao feed a própria lista de exclusivos em vez de um filtro, para que a
 * sequência continue nas outras captações do mesmo corretor.
 */
function handleExploreVideo(property: Property, index: number): void {
	rememberCarouselPosition();
	trackItemClicked(property, index);

	openShortsFeed({
		at: property.commercialId,
		page: exclusives.snapshot(property.commercialId),
		entrySource: SHORTS_ENTRY_SOURCE.AGENT_PROFILE
	});
}

function handleDetailsClick(property: Property, index: number): void {
	rememberCarouselPosition();
	trackItemClicked(property, index);
}

/**
 * "Ver detalhes", "Ver todos" e "Explorar em vídeo" são navegações de rota, e a
 * página do perfil remonta na volta. O scroll vertical o router já restaura;
 * o slide do carrossel precisa ser guardado à parte, senão o visitante sempre
 * reencontra a seção no primeiro imóvel.
 */
const sliderRef = ref<{ currentCarouselItemIndex: number } | null>(null);
const restoredIndex = ref(0);

/**
 * Entre a saída e a volta a lista pode encolher — basta uma captação ser
 * despublicada. Sem o teto, o carrossel receberia um índice que não existe mais
 * e a restauração silenciosamente não aconteceria.
 */
const initialIndex = computed<number>(() =>
	properties.value.length
		? Math.min(restoredIndex.value, properties.value.length - 1)
		: 0
);

/**
 * Chamada só na saída para um dos três destinos do critério. É o que impede que
 * entrar no perfil por outro caminho, na mesma aba, caia no meio do carrossel.
 */
function rememberCarouselPosition(): void {
	const index = sliderRef.value?.currentCarouselItemIndex;

	if (typeof index !== 'number') return;

	saveAgentExclusivesCarouselIndex(props.agentSlug, index);
}

onMounted(() => {
	/**
	 * Aplicada só quando o visitante voltou de um dos três destinos, e consumida
	 * de uma vez: vale para aquela volta, não para a sessão toda nem para uma
	 * entrada nova no perfil pela home.
	 */
	if (isReturnNavigation()) {
		restoredIndex.value = getAgentExclusivesCarouselIndex(props.agentSlug);
	}

	clearAgentExclusivesCarouselIndex(props.agentSlug);

	void exclusives.load();
});
const hasContent = computed<boolean>(() => hasProperties.value);

defineExpose({ hasContent });
</script>

<template>
	<SectionSkeleton v-if="isInitialLoading" :class="props.class" />

	<section
		v-else-if="hasProperties"
		:class="
			cn(
				'w-full border-b border-white-dark-3 bg-white pb-8 md:pb-12',
				props.class
			)
		"
		data-testid="agent-exclusive-properties-section"
	>
		<StandardSlider
			ref="sliderRef"
			:items="properties"
			:initial-index="initialIndex"
			:is-loading="isInitialLoading"
			:tracking-context="trackingContext"
			:section-name="SECTION_NAME"
			carousel-item-size="full"
			container-class="p-0!"
			:carousel-options="{ class: 'gap-6!' }"
			footer-class="min-h-[104px] md:min-h-12"
			@navigate="trackNavigated"
		>
			<template #header>
				<header class="flex items-center gap-4 pb-6">
					<div class="grid size-7 shrink-0 place-items-center bg-primary">
						<IconMaterialSymbolsLightLockOutline
							class="size-4 text-white"
							aria-hidden="true"
						/>
					</div>
					<h2
						class="font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary lg:text-[32px] lg:tracking-[-0.64px]"
					>
						Captações exclusivas
					</h2>
				</header>
			</template>

			<template #items="{ item, index }">
				<ExclusivePropertyCard
					:property="item"
					:card-position="index"
					:position-in-list="index"
					:section="SECTION_NAME"
					:tracking-context="trackingContext"
					@explore-video="handleExploreVideo(item, index)"
					@details-click="handleDetailsClick(item, index)"
				/>
			</template>

			<template #cta>
				<!-- Só depois da resposta: durante o carregamento o total ainda é 0. -->
				<Button
					v-if="viewAllHref && hasProperties && hasMoreThanCarousel"
					as-child
					variant="secondary"
					size="md"
					class="h-12 w-fit shrink-0"
					data-testid="agent-exclusive-properties-view-all"
				>
					<NuxtLink :to="viewAllHref" @click="handleViewAllClick">
						<span>{{ VIEW_ALL_CTA_TEXT }}</span>
						<Badge
							variant="tertiary"
							class="h-5 w-auto rounded-full px-2 py-0 text-xs"
						>
							{{ total }}
						</Badge>
					</NuxtLink>
				</Button>
			</template>
		</StandardSlider>
	</section>
</template>
